import { INTERACT, PLAYER, SHIP, WORLD } from "./config.ts";
import { approach, clamp, localToWorld, wrapAngle, type Pose, type Vec3 } from "./math.ts";
import { BERTHS, COLLIDERS, DOCK_ZONE_RADIUS, nearestOutpost, coastRadius, terrainHeight, type BoxCollider, type Landmass } from "./world.ts";

/**
 * Ship-local frame: origin at the waterline centre, +z toward the bow, +x to
 * starboard, y up. The waist (main) deck top is at SHIP.deckHeight; the aft
 * quarterdeck and forward forecastle are raised above it and reached by stairs.
 */

const D = SHIP.deckHeight;
/** Quarterdeck and forecastle tops. */
const QD = D + SHIP.quarterdeckRise;
const FC = D + SHIP.forecastleRise;
const HULL_BOTTOM = -SHIP.draft;

function box(minX: number, maxX: number, minZ: number, maxZ: number, y0: number, y1: number): BoxCollider {
  return { kind: "box", minX, maxX, minZ, maxZ, y0, y1, blocksShip: false };
}

/** Solid hull volumes; their tops form the walkable decks (waist, quarterdeck, forecastle). */
export const HULL_BOXES: readonly BoxCollider[] = [
  box(-3, 3, -6, 6, HULL_BOTTOM, D),
  box(-3, 3, -9.5, -6, HULL_BOTTOM, QD),
  box(-2.2, 2.2, 6, 9, HULL_BOTTOM, FC),
];

/** Bulwark rail height above each deck top (tools/models/ship.py `RAIL_H`). */
export const RAIL_H = 0.95;
const RAIL_T = 0.12;

/** Cannon mounts on the waist, ship-local; `side` is -1 for port and 1 for starboard. */
const CANNONS = [
  { x: -2.4, z: 4, side: -1 },
  { x: 2.4, z: 4, side: 1 },
  { x: -2.4, z: 2, side: -1 },
  { x: 2.4, z: 2, side: 1 },
  { x: -2.4, z: -3, side: -1 },
  { x: 2.4, z: -3, side: 1 },
] as const;

/** Stair treads, solid down to the waist deck; each rise is 0.4 m (≤ PLAYER.stepHeight). */
const STAIRS: readonly BoxCollider[] = [
  ...[-1, 1].flatMap((s) => {
    const x0 = Math.min(s * 1.6, s * 2.6), x1 = Math.max(s * 1.6, s * 2.6);
    return [box(x0, x1, -5.4, -4.8, D, D + 0.4), box(x0, x1, -6, -5.4, D, D + 0.8)];
  }),
  box(-0.6, 0.6, 5.4, 6, D, D + 0.4),
];

/**
 * Deck furniture and rails (ship-local). Both waist rails have a gangway gap at z ∈ [-1, 1];
 * the quarterdeck breast rail leaves stair openings at |x| 1.6–2.88, the forecastle's at |x| < 0.6.
 */
export const SHIP_FIXTURES: readonly BoxCollider[] = [
  // Waist rails (gangway gap) and the shoulder rails beside the forecastle.
  box(-3, -3 + RAIL_T, -6, -1, D, D + RAIL_H),
  box(-3, -3 + RAIL_T, 1, 6, D, D + RAIL_H),
  box(3 - RAIL_T, 3, -6, -1, D, D + RAIL_H),
  box(3 - RAIL_T, 3, 1, 6, D, D + RAIL_H),
  box(-3, -2.2, 6 - RAIL_T, 6, D, D + RAIL_H),
  box(2.2, 3, 6 - RAIL_T, 6, D, D + RAIL_H),
  // Quarterdeck rails: sides, stern and the breast rail between the stairs.
  box(-3, -3 + RAIL_T, -9.5, -6, QD, QD + RAIL_H),
  box(3 - RAIL_T, 3, -9.5, -6, QD, QD + RAIL_H),
  box(-3, 3, -9.5, -9.5 + RAIL_T, QD, QD + RAIL_H),
  box(-1.6, 1.6, -6 - RAIL_T, -6, QD, QD + RAIL_H),
  // Forecastle rails: sides, bow and the breast rail either side of its stair.
  box(-2.2, -2.2 + RAIL_T, 6, 9, FC, FC + RAIL_H),
  box(2.2 - RAIL_T, 2.2, 6, 9, FC, FC + RAIL_H),
  box(-2.2, 2.2, 9 - RAIL_T, 9, FC, FC + RAIL_H),
  box(-2.2, -0.6, 6, 6 + RAIL_T, FC, FC + RAIL_H),
  box(0.6, 2.2, 6, 6 + RAIL_T, FC, FC + RAIL_H),
  ...STAIRS,
  // Main mast and fore mast.
  box(-0.3, 0.3, 0.5, 1.1, D, D + 15),
  box(-0.22, 0.22, 7.18, 7.62, FC, FC + 11),
  // Crow's nest platform and its rails.
  box(-1.2, 1.2, -0.4, 2, D + 8.85, D + 9),
  box(-1.2, 1.2, -0.4, -0.28, D + 9, D + 10),
  box(-1.2, 1.2, 1.88, 2, D + 9, D + 10),
  box(-1.2, -1.08, -0.4, 2, D + 9, D + 10),
  box(1.08, 1.2, -0.4, 2, D + 9, D + 10),
  // Quarterdeck: wheel pedestal, capstan, supply crate, map table.
  box(-0.3, 0.3, -7.7, -7.3, QD, QD + 0.9),
  box(1.15, 1.85, -8.35, -7.65, QD, QD + 0.8),
  box(-1.85, -1.15, -8.45, -7.75, QD, QD + 0.7),
  box(-1.5, -0.7, -6.95, -6.35, QD, QD + 0.95),
  // Bilge pump.
  box(-0.3, 0.3, 2.7, 3.3, D, D + 1.0),
  // Stores (hold-tier props) either side of the forecastle stair.
  box(0.8, 1.9, 5.15, 5.9, D, D + 0.9),
  box(-1.9, -0.8, 5.15, 5.9, D, D + 0.9),
  // Belfry on the forecastle.
  box(1.25, 1.65, 6.15, 6.45, FC, FC + 1.9),
  // Cannons: carriage plus the shot rack forward of it (clear of the leak repair spots).
  ...CANNONS.map((c) => box(c.x - 0.35, c.x + 0.35, c.z - 0.35, c.z + 0.65, D, D + 0.8)),
];

export const SHIP_COLLIDERS: readonly BoxCollider[] = [...HULL_BOXES, ...SHIP_FIXTURES];

/** Interaction anchor points, ship-local. */
export const SHIP_POINTS = {
  wheel: { x: 0, y: QD + 1.0, z: -7.2 },
  /** Where the helmsman stands. */
  helm: { x: 0, y: QD, z: -8.4 },
  capstan: { x: 1.5, y: QD + 0.8, z: -8 },
  /** The bucket rack beside the bilge pump (the authored bucket in ship.py). */
  bucketRack: { x: 0.57, y: D + 0.45, z: 3.04 },
  supplies: { x: -1.5, y: QD + 0.7, z: -8.1 },
  /** The chart on the map table (client-only: opens the map). */
  mapTable: { x: -1.1, y: QD + 1, z: -6.65 },
  bell: { x: 1.45, y: FC + 1.4, z: 6.3 },
  /** Stern, quarterdeck breast, main mast and bow lanterns (`ShipState.lanterns` bit i). */
  lanterns: [
    { x: 0, y: QD + 1.55, z: -9.55 },
    { x: 1.3, y: QD + 1.3, z: -6.06 },
    { x: 0, y: D + 2.1, z: 1.25 },
    { x: 0, y: FC + 1.3, z: 9.05 },
  ],
  /** Crow's nest telescope eyepiece and where its user stands. */
  nestScope: { x: 0, y: D + 10.1, z: 1.9 },
  nestStand: { x: 0, y: D + 9, z: 1.5 },
  /** The hull ladder hangs in the starboard gangway (rail gap z ∈ [-1, 1]) and ends level with the waist. */
  ladderTop: { x: 2.3, y: D, z: 0 },
  /** Swimmers grab it near the waterline. */
  ladderBottom: { x: 3.4, y: -0.4, z: 0 },
  leaks: [
    { x: -2.6, y: D + 0.15, z: 3.0 },
    { x: 2.6, y: D + 0.15, z: 3.0 },
    { x: -2.6, y: D + 0.15, z: -4.1 },
    { x: 2.6, y: D + 0.15, z: -4.1 },
  ],
  /** Cannon mounts: `point` is the interaction anchor, `station` where the gunner stands. */
  cannons: CANNONS.map((c) => ({
    x: c.x,
    z: c.z,
    side: c.side,
    point: { x: c.x, y: D + 0.8, z: c.z },
    station: { x: c.side * 1.4, y: D, z: c.z },
  })),
  spawns: [
    { x: -0.9, y: D, z: -2.4 },
    { x: 0.9, y: D, z: -2.4 },
    { x: -0.9, y: D, z: -1.2 },
    { x: 0.9, y: D, z: -1.2 },
  ],
} as const;

/**
 * A climbable ladder in its own frame: the climber hangs on the +x side of the rungs,
 * facing -x, climbs from `bottom` to `head`, crosses over to above `top`, then steps down
 * onto it. Rungs lie `rungOut` metres on the -x side of the climber's path.
 */
export interface LadderSpec {
  bottom: Vec3;
  head: Vec3;
  top: Vec3;
  /** Height of the lowest rung and the rung spacing. */
  rungBase: number;
  rungStep: number;
  rungOut: number;
  /** Two-hand crest grip, spread ±crestSpread along z. */
  crest: Vec3;
  crestSpread: number;
  seconds: number;
}

/**
 * Starboard hull ladder: up from the water to a top step level with the waist, then straight in
 * through the gangway. Its rails stand on past the deck as handholds for the step aboard.
 */
export const SHIP_LADDER: LadderSpec = {
  bottom: SHIP_POINTS.ladderBottom,
  head: { x: SHIP_POINTS.ladderBottom.x, y: D + 0.02, z: SHIP_POINTS.ladderBottom.z },
  top: SHIP_POINTS.ladderTop,
  /** Six rungs, then the top step flush with the deck six steps up (tools/models/ship.py). */
  rungBase: D - 6 * 0.45,
  rungStep: 0.45,
  rungOut: 0.22,
  crest: { x: 3.18, y: D + 0.85, z: SHIP_POINTS.ladderBottom.z },
  crestSpread: 0.3,
  seconds: INTERACT.ladderSeconds,
};

/**
 * Main-mast ladder on the starboard face: from the waist up through the lubber hole to the nest
 * floor, holding the ladder's rails where they stand on above the platform (to D + 10.05).
 */
export const MAST_LADDER: LadderSpec = {
  bottom: { x: 0.66, y: D, z: 0.8 },
  head: { x: 0.66, y: D + 9.02, z: 0.8 },
  /** Between the mast (x 0.3) and the nest rail (x 1.08) with the capsule clear of both. */
  top: { x: 0.69, y: D + 9, z: 0.8 },
  rungBase: D + 0.3,
  rungStep: 0.45,
  rungOut: 0.22,
  crest: { x: 0.44, y: D + 9.85, z: 0.8 },
  crestSpread: 0.2,
  seconds: 5.5,
};

/** Ladders aboard every ship; `PlayerState.shipLadder` indexes this list. */
export const SHIP_LADDERS = [SHIP_LADDER, MAST_LADDER] as const;

/** Rung climb (first 75%), crossover (to 90%) and step down, evaluated in the ladder's frame. */
export function ladderPosition(progress: number, out: Vec3, spec: LadderSpec = SHIP_LADDER): Vec3 {
  const p = clamp(progress, 0, 1);
  const { bottom, head, top } = spec;
  const smooth = (v: number) => v * v * (3 - 2 * v);
  if (p <= 0.75) {
    const t = smooth(p / 0.75);
    out.x = bottom.x + (head.x - bottom.x) * t;
    out.y = bottom.y + (head.y - bottom.y) * t;
    out.z = bottom.z + (head.z - bottom.z) * t;
  } else if (p <= 0.9) {
    const t = smooth((p - 0.75) / 0.15);
    out.x = head.x + (top.x - head.x) * t;
    out.z = head.z + (top.z - head.z) * t;
    out.y = head.y;
  } else {
    out.x = top.x;
    out.z = top.z;
    out.y = top.y + (head.y - top.y) * (1 - smooth((p - 0.9) / 0.1));
  }
  return out;
}

/** True when the ship-local point (x, z) is over a deck. */
export function overDeck(lx: number, lz: number): boolean {
  for (const b of HULL_BOXES) {
    if (lx >= b.minX && lx <= b.maxX && lz >= b.minZ && lz <= b.maxZ) return true;
  }
  return false;
}

/** Top of the highest deck over ship-local (x, z), or -Infinity off the hull. */
export function deckTopAt(lx: number, lz: number): number {
  let top = -Infinity;
  for (const b of HULL_BOXES) {
    if (lx >= b.minX && lx <= b.maxX && lz >= b.minZ && lz <= b.maxZ) top = Math.max(top, b.y1);
  }
  return top;
}

export interface DeckLadderEnd {
  spec: LadderSpec;
  end: "bottom" | "top";
}

/**
 * The ladder end a sailor standing on a deck at ship-local (lx, ly, lz) touches: the mast
 * ladder's foot in the waist or its head in the crow's nest, or the hull ladder's head at the
 * gangway. Ends on a deck are tight, so crossing the busy waist past one never touches it.
 */
export function deckLadderEnd(lx: number, ly: number, lz: number): DeckLadderEnd | null {
  const reach = INTERACT.deckLadderReach;
  const near = (p: Vec3, r: number) => Math.abs(ly - p.y) < 0.5 && Math.hypot(lx - p.x, lz - p.z) <= r;
  if (near(MAST_LADDER.bottom, reach)) return { spec: MAST_LADDER, end: "bottom" };
  if (near(MAST_LADDER.top, 1.2)) return { spec: MAST_LADDER, end: "top" };
  if (near(SHIP_LADDER.top, reach)) return { spec: SHIP_LADDER, end: "top" };
  return null;
}

/**
 * Outboard deck edges (ship-local) with their outward normals: the perimeter of the
 * HULL_BOXES union. Each box side is split at the other boxes' extents and a piece is
 * kept only when just outside its midpoint is off every deck, so internal joins drop out.
 */
const DECK_EDGES = (() => {
  const edges: { ax: number; az: number; bx: number; bz: number; nx: number; nz: number }[] = [];
  for (const b of HULL_BOXES) {
    const sides = [
      { fixed: b.minX, lo: b.minZ, hi: b.maxZ, nx: -1, nz: 0 },
      { fixed: b.maxX, lo: b.minZ, hi: b.maxZ, nx: 1, nz: 0 },
      { fixed: b.minZ, lo: b.minX, hi: b.maxX, nx: 0, nz: -1 },
      { fixed: b.maxZ, lo: b.minX, hi: b.maxX, nx: 0, nz: 1 },
    ];
    for (const side of sides) {
      const alongX = side.nz !== 0;
      const cuts = [side.lo, side.hi];
      for (const o of HULL_BOXES) {
        for (const v of alongX ? [o.minX, o.maxX] : [o.minZ, o.maxZ]) if (v > side.lo && v < side.hi) cuts.push(v);
      }
      cuts.sort((p, q) => p - q);
      for (let i = 0; i + 1 < cuts.length; i++) {
        const a = cuts[i]!, c = cuts[i + 1]!, mid = (a + c) / 2;
        const px = alongX ? mid : side.fixed + side.nx * 0.01, pz = alongX ? side.fixed + side.nz * 0.01 : mid;
        if (c - a < 1e-6 || overDeck(px, pz)) continue;
        edges.push(alongX
          ? { ax: a, az: side.fixed, bx: c, bz: side.fixed, nx: side.nx, nz: side.nz }
          : { ax: side.fixed, az: a, bx: side.fixed, bz: c, nx: side.nx, nz: side.nz });
      }
    }
  }
  return edges;
})();

/**
 * Bot walking graph over the decks: a lane each side of the waist (x ±1) up the
 * quarterdeck stairs and round the wheel pedestal, plus centreline links. Each side's
 * nodes are n0…n10 in order (starboard first), then the two centreline nodes.
 */
export const SHIP_NAV: { readonly nodes: readonly Vec3[]; readonly edges: readonly (readonly [number, number])[] } = (() => {
  const nodes: Vec3[] = [], edges: [number, number][] = [];
  for (const s of [1, -1]) {
    const base = nodes.length;
    // The port lane passes aft of the wheel a little further from the map table.
    const aft = s > 0 ? -7.2 : -7.35;
    nodes.push(
      { x: s * 1, y: D, z: 4.6 }, { x: s * 1, y: D, z: 3 }, { x: s * 1, y: D, z: 0 }, { x: s * 1, y: D, z: -2 },
      { x: s * 1, y: D, z: -4.2 }, { x: s * 2.1, y: D + 0.4, z: -5.1 }, { x: s * 2.1, y: D + 0.8, z: -5.7 },
      { x: s * 2.1, y: QD, z: -6.6 }, { x: s * 2.1, y: QD, z: aft }, { x: s * 0.72, y: QD, z: aft }, { x: s * 0.72, y: QD, z: -8.8 },
    );
    for (let i = 0; i < 10; i++) edges.push([base + i, base + i + 1]);
  }
  const c0 = nodes.push({ x: 0, y: D, z: -1.6 }) - 1, c1 = nodes.push({ x: 0, y: D, z: 4.6 }) - 1;
  edges.push([c0, 2], [c0, 13], [c0, 3], [c0, 14], [c1, 0], [c1, 11], [10, 21]);
  return { nodes, edges };
})();

const NAV_LINKS: readonly number[][] = SHIP_NAV.nodes.map((_, i) => SHIP_NAV.edges.flatMap(([a, b]) => (a === i ? [b] : b === i ? [a] : [])));

function nearestNavNode(p: Vec3): number {
  let best = -1, bestD = Infinity;
  SHIP_NAV.nodes.forEach((n, i) => {
    const d = Math.hypot(n.x - p.x, n.z - p.z);
    if (Math.abs(n.y - p.y) < 0.3 && d < bestD) { bestD = d; best = i; }
  });
  return best;
}

/**
 * Ship-local waypoints from `from` to `to` over SHIP_NAV (breadth-first), ending at `to`.
 * Falls back to the straight `[to]` when either end has no node on its level.
 */
export function shipNavPath(from: Vec3, to: Vec3, out: Vec3[]): Vec3[] {
  out.length = 0;
  const start = nearestNavNode(from), goal = nearestNavNode(to);
  if (start >= 0 && goal >= 0) {
    const prev = new Array<number>(SHIP_NAV.nodes.length).fill(-2);
    prev[start] = -1;
    const queue = [start];
    for (let q = 0; q < queue.length && prev[goal] === -2; q++) {
      for (const next of NAV_LINKS[queue[q]!]!) if (prev[next] === -2) { prev[next] = queue[q]!; queue.push(next); }
    }
    for (let i = goal; i >= 0 && prev[goal] !== -2; i = prev[i]!) out.unshift(SHIP_NAV.nodes[i]!);
  }
  out.push(to);
  return out;
}

/** Where a thrown bucket's water leaves the deck: the edge point and its outward normal (ship-local). */
export interface OverboardThrow {
  x: number;
  z: number;
  nx: number;
  nz: number;
}

/**
 * True when a sailor standing at ship-local (lx, lz) facing ship-local `yaw` is at the
 * rail (within SHIP.bucketRailReach of an outboard deck edge) and facing the sea. Fills
 * `out` with the nearest such edge point and its outward normal.
 */
export function overboardThrow(lx: number, lz: number, yaw: number, out: OverboardThrow): boolean {
  if (!overDeck(lx, lz)) return false;
  const fx = Math.sin(yaw), fz = Math.cos(yaw);
  let best: number = SHIP.bucketRailReach;
  let found = false;
  for (const e of DECK_EDGES) {
    if (fx * e.nx + fz * e.nz < SHIP.bucketFacingCos) continue;
    const dx = e.bx - e.ax, dz = e.bz - e.az;
    const t = clamp(((lx - e.ax) * dx + (lz - e.az) * dz) / (dx * dx + dz * dz), 0, 1);
    const px = e.ax + dx * t, pz = e.az + dz * t;
    const d = Math.hypot(lx - px, lz - pz);
    if (d > best) continue;
    best = d;
    found = true;
    out.x = px; out.z = pz; out.nx = e.nx; out.nz = e.nz;
  }
  return found;
}

export interface ShipSim extends Pose {
  speed: number;
  rudder: number;
  /** 0 stowed, 1 = ⅓, 2 = ⅔, 3 full. */
  sail: number;
  anchored: boolean;
  /** Deceleration locked in when the anchor dropped. */
  anchorDecel: number;
  /** 0 catted at the bow … 1 fully paid out. Always 0 while not anchored. */
  anchorDepth: number;
  /** The pawl is off and the anchor is running out freely. */
  anchorFalling: boolean;
  /** Depth per second of the free-running fall. */
  anchorFallSpeed: number;
  docked: boolean;
  /** Seconds spent overlapping an obstacle (for unstick). */
  stuckTime: number;
  /** World-frame sideways shove (m/s) from being rammed; decays at SHIP.shoveDrag. */
  shoveX: number;
  shoveZ: number;
}

export function createShip(pose: Pose = BERTHS[0]!): ShipSim {
  return {
    x: pose.x,
    z: pose.z,
    heading: pose.heading,
    speed: 0,
    rudder: 0,
    sail: 0,
    anchored: true,
    anchorDecel: 0,
    anchorDepth: 1,
    anchorFalling: false,
    anchorFallSpeed: 0,
    docked: true,
    stuckTime: 0,
    shoveX: 0,
    shoveZ: 0,
  };
}

export interface ShipStepInput {
  /** Rudder input from the helmsman, -1 (port) … 1 (starboard). */
  steer: number;
  /** Someone is aboard or at the wheel. */
  crewed: boolean;
  /** Fraction (0…1) of the sail setting's speed the rig still delivers (`shipDrive`). */
  drive: number;
}

export interface ShipStepResult {
  /** Impact speed of a collision this step (0 when none). */
  impact: number;
  /** Index into `ShipWorld.others` of the ship hit this step, or -1. */
  hitShip: number;
  /** World point where the hulls (or hull and obstacle) met, valid when `impact` > 0. */
  contactX: number;
  contactZ: number;
  /** The ship was pushed out of an obstacle by the unstick rule. */
  unstuck: boolean;
}

/** Per-ship surroundings for `stepShip`. */
export interface ShipWorld {
  /** The crew's own berth (docking target). */
  berth: Pose;
  /** Poses of the other ships in the room. */
  others: readonly Pose[];
}

const MAX_SPEED = SHIP.sailSpeeds[SHIP.sailSpeeds.length - 1]!;
const BOUND = WORLD.half - 20;
const tmp: Vec3 = { x: 0, y: 0, z: 0 };
const tmpOther: Vec3 = { x: 0, y: 0, z: 0 };

/** First hull overlap: outward normal, the index of the ship hit (-1 for terrain and rocks) and the world contact point. */
export interface HullContact { nx: number; nz: number; other: number; x: number; z: number }

/**
 * Returns the first obstacle the hull overlaps at `pose`, or null when clear: its outward
 * normal (x, z) and contact point. Terrain shallower than the draft counts, and so do the
 * hulls in `others` (`other` is the index of the ship hit, -1 for terrain and rocks).
 */
export function hullOverlap(pose: Pose, others: readonly Pose[] = []): HullContact | null {
  for (const c of SHIP.hullCircles) {
    localToWorld(pose, 0, 0, c.z, tmp);
    const cx = tmp.x;
    const cz = tmp.z;
    for (let k = 0; k < others.length; k++) {
      const o = others[k]!;
      if (Math.abs(o.x - cx) > SHIP.length || Math.abs(o.z - cz) > SHIP.length) continue;
      for (const oc of SHIP.hullCircles) {
        localToWorld(o, 0, 0, oc.z, tmpOther);
        const dx = cx - tmpOther.x;
        const dz = cz - tmpOther.z;
        const d = Math.sqrt(dx * dx + dz * dz);
        if (d >= c.r + oc.r) continue;
        // The hulls meet on the line between the circle centres, split by their radii.
        const k0 = c.r / (c.r + oc.r);
        return d > 1e-6 ? { nx: dx / d, nz: dz / d, other: k, x: cx - dx * k0, z: cz - dz * k0 } : { nx: 1, nz: 0, other: k, x: cx, z: cz };
      }
    }
    for (const col of COLLIDERS) {
      if (!col.blocksShip || col.kind !== "cyl") continue;
      const dx = cx - col.x;
      const dz = cz - col.z;
      const d = Math.sqrt(dx * dx + dz * dz);
      if (d < c.r + col.r) return d > 1e-6 ? { nx: dx / d, nz: dz / d, other: -1, x: cx - (dx / d) * c.r, z: cz - (dz / d) * c.r } : { nx: 1, nz: 0, other: -1, x: cx, z: cz };
    }
    // Sample the circle centre and rim for shallow terrain.
    for (let i = -1; i < 8; i++) {
      const a = (i * Math.PI) / 4;
      const sx = i < 0 ? cx : cx + Math.cos(a) * c.r;
      const sz = i < 0 ? cz : cz + Math.sin(a) * c.r;
      if (terrainHeight(sx, sz) > -SHIP.draft) {
        // Normal: downhill direction (finite difference).
        const e = 1.5;
        const gx = terrainHeight(sx + e, sz) - terrainHeight(sx - e, sz);
        const gz = terrainHeight(sx, sz + e) - terrainHeight(sx, sz - e);
        const len = Math.sqrt(gx * gx + gz * gz);
        return len > 1e-6 ? { nx: -gx / len, nz: -gz / len, other: -1, x: sx, z: sz } : { nx: cx - sx || 1, nz: cz - sz, other: -1, x: sx, z: sz };
      }
    }
  }
  return null;
}

/** Nearest clear pose found by stepping outward along `dir`, then a spiral search. */
export function findOpenWater(pose: Pose, nx: number, nz: number, others: readonly Pose[] = []): Pose {
  for (let d = 2; d <= 80; d += 2) {
    const p = { x: pose.x + nx * d, z: pose.z + nz * d, heading: pose.heading };
    if (!hullOverlap(p, others)) return p;
  }
  for (let d = 10; d <= 200; d += 10) {
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2;
      const p = { x: pose.x + Math.cos(a) * d, z: pose.z + Math.sin(a) * d, heading: pose.heading };
      if (!hullOverlap(p, others)) return p;
    }
  }
  return { ...BERTHS[0]! };
}

export function inDockZone(pose: Pose, berth: Pose): boolean {
  return Math.hypot(pose.x - berth.x, pose.z - berth.z) <= DOCK_ZONE_RADIUS;
}

/**
 * Clear-water pose just off an island's coast, broadside to the shore on the
 * side facing the outpost. Used for respawn-free test teleports.
 */
export function anchoragePose(l: Landmass, clearance = 4): Pose {
  const outpost = nearestOutpost(l.cx, l.cz).landmass;
  const dx = outpost.cx - l.cx;
  const dz = outpost.cz - l.cz;
  const len = Math.sqrt(dx * dx + dz * dz);
  const ux = len === 0 ? 0 : dx / len;
  const uz = len === 0 ? 1 : dz / len;
  const heading = Math.atan2(-uz, ux);
  const coast = coastRadius(l, Math.atan2(uz, ux));
  for (let d = coast; d < coast * 3; d += 1) {
    const p = { x: l.cx + ux * d, z: l.cz + uz * d, heading };
    const probe = { x: p.x + ux * clearance, z: p.z + uz * clearance, heading };
    if (!hullOverlap(p) && !hullOverlap({ x: p.x - ux * clearance, z: p.z - uz * clearance, heading }) && !hullOverlap(probe)) {
      return { x: p.x + ux * clearance, z: p.z + uz * clearance, heading };
    }
  }
  return { x: l.cx + ux * coast * 2, z: l.cz + uz * coast * 2, heading };
}

/**
 * One fixed step of the kinematic ship model. Deterministic for identical
 * inputs. Mutates `ship`.
 */
export function stepShip(ship: ShipSim, input: ShipStepInput, dt: number, result: ShipStepResult, world: ShipWorld): ShipStepResult {
  result.impact = 0;
  result.hitShip = -1;
  result.unstuck = false;
  const berth = world.berth;

  if (!input.crewed) ship.sail = 0;
  ship.rudder = approach(ship.rudder, input.crewed ? clamp(input.steer, -1, 1) : 0, SHIP.rudderRate * dt);

  // Docking: an anchored ship inside its dock zone eases onto its berth.
  if (ship.anchored && inDockZone(ship, berth)) {
    ship.speed = approach(ship.speed, 0, Math.max(ship.anchorDecel, SHIP.drag) * dt);
    const dx = berth.x - ship.x;
    const dz = berth.z - ship.z;
    const d = Math.sqrt(dx * dx + dz * dz);
    const step = 2.5 * dt;
    if (d > step) {
      ship.x += (dx / d) * step;
      ship.z += (dz / d) * step;
    } else {
      ship.x = berth.x;
      ship.z = berth.z;
    }
    const dh = wrapAngle(berth.heading - ship.heading);
    const hs = 0.35 * dt;
    ship.heading = wrapAngle(ship.heading + clamp(dh, -hs, hs));
    ship.docked = d <= step && Math.abs(dh) <= hs;
    ship.stuckTime = 0;
    ship.shoveX = 0;
    ship.shoveZ = 0;
    return result;
  }
  ship.docked = false;

  if (ship.anchored) {
    ship.speed = approach(ship.speed, 0, Math.max(ship.anchorDecel, 0.2) * dt);
  } else if (!input.crewed) {
    ship.speed = approach(ship.speed, 0, (MAX_SPEED / SHIP.emptyStopTime) * dt);
  } else {
    // A damaged rig both caps the speed and gathers way more slowly.
    const drive = clamp(input.drive, 0, 1);
    const target = (SHIP.sailSpeeds[ship.sail] ?? 0) * drive;
    ship.speed = ship.speed < target ? Math.min(ship.speed + SHIP.acceleration * drive * dt, target) : Math.max(ship.speed - SHIP.drag * dt, target);
  }

  const absSpeed = Math.abs(ship.speed);
  // Steerage ramps up from the minimum steering speed to full at `fullSteerSpeed`.
  const steerage = absSpeed < SHIP.minSteerSpeed ? 0 : Math.min(1, (absSpeed - SHIP.minSteerSpeed) / (SHIP.fullSteerSpeed - SHIP.minSteerSpeed));
  const turnRate = SHIP.maxTurnRate * ship.rudder * steerage * Math.sign(ship.speed);

  const prevX = ship.x;
  const prevZ = ship.z;
  const prevH = ship.heading;
  const wasOverlapping = hullOverlap(ship, world.others) !== null;

  ship.heading = wrapAngle(ship.heading + turnRate * dt);
  ship.x += (Math.sin(ship.heading) * ship.speed + ship.shoveX) * dt;
  ship.z += (Math.cos(ship.heading) * ship.speed + ship.shoveZ) * dt;
  // A rammed hull's shove bleeds off through the water.
  const shove = Math.hypot(ship.shoveX, ship.shoveZ);
  if (shove > 0) {
    const keep = Math.max(0, shove - SHIP.shoveDrag * dt) / shove;
    ship.shoveX *= keep;
    ship.shoveZ *= keep;
  }

  // Map boundary: clamp position; the tangential part of the motion survives.
  ship.x = clamp(ship.x, -BOUND, BOUND);
  ship.z = clamp(ship.z, -BOUND, BOUND);

  const hit = hullOverlap(ship, world.others);
  if (hit && !wasOverlapping) {
    const dirX = Math.sin(ship.heading) * Math.sign(ship.speed);
    const dirZ = Math.cos(ship.heading) * Math.sign(ship.speed);
    const impact = Math.max(0, -(dirX * hit.nx + dirZ * hit.nz)) * absSpeed;
    ship.x = prevX + hit.nx * 0.05;
    ship.z = prevZ + hit.nz * 0.05;
    ship.heading = prevH;
    // The hull fends off what it touched instead of grinding against it: a ship nosed into a pier
    // drifts clear and can gather way and steer out again.
    ship.shoveX = hit.nx * SHIP.fendOffSpeed;
    ship.shoveZ = hit.nz * SHIP.fendOffSpeed;
    if (impact > 0.3) {
      result.impact = impact;
      result.hitShip = hit.other;
      result.contactX = hit.x;
      result.contactZ = hit.z;
      ship.speed = -Math.sign(ship.speed) * Math.min(SHIP.bounceSpeed, absSpeed * 0.5);
    } else {
      ship.speed *= 0.8;
    }
  }

  if (hit && wasOverlapping && absSpeed < SHIP.unstickMaxSpeed) {
    ship.stuckTime += dt;
    if (ship.stuckTime >= SHIP.unstickDelay) {
      const p = findOpenWater(ship, hit.nx, hit.nz, world.others);
      ship.x = p.x;
      ship.z = p.z;
      ship.speed = 0;
      ship.stuckTime = 0;
      result.unstuck = true;
    }
  } else if (!hit) {
    ship.stuckTime = 0;
  }
  return result;
}

/** Kicks the capstan pawl off: the anchor runs out freely and the ship starts braking at once. */
export function dropAnchor(ship: ShipSim): void {
  ship.anchored = true;
  ship.anchorDecel = Math.abs(ship.speed) / SHIP.anchorStopTime;
  ship.anchorFalling = true;
  ship.anchorFallSpeed = 0;
}

/** Catted anchor pivot (tools/models/ship.py `anchor`), ship-local, and how far it pays out. */
export const ANCHOR = { x: 3.0, y: 1.2, z: 7.0, drop: 3.4, flukeBelowPivot: 0.57 } as const;
/** Depth at which the falling flukes meet the waterline (ship-local y = 0). */
export const ANCHOR_SPLASH_DEPTH = (ANCHOR.y - ANCHOR.flukeBelowPivot) / ANCHOR.drop;

/** `stepAnchor` event bits. */
export const ANCHOR_CLICK = 1;
export const ANCHOR_SPLASH = 2;
export const ANCHOR_BOTTOM = 4;
export const ANCHOR_STOWED = 8;

/** Depth per second raised by `pushers` crewmates on the capstan bars. */
export function anchorRaiseRate(pushers: number): number {
  return pushers <= 0 ? 0 : (1 + (pushers - 1) * SHIP.capstanHelperBoost) / SHIP.anchorRaiseSeconds;
}

/**
 * One fixed step of the anchor: a free fall after the pawl is kicked, otherwise wound in by
 * the crewmates on the bars (none pausing it where it hangs). Returns `ANCHOR_*` event bits;
 * the ship weighs anchor when it is catted again.
 */
export function stepAnchor(ship: ShipSim, pushers: number, dt: number): number {
  if (!ship.anchored) {
    ship.anchorDepth = 0;
    ship.anchorFalling = false;
    ship.anchorFallSpeed = 0;
    return 0;
  }
  const before = ship.anchorDepth;
  let events = 0;
  if (ship.anchorFalling) {
    ship.anchorFallSpeed += (2 / SHIP.anchorDropSeconds ** 2) * dt;
    ship.anchorDepth = Math.min(1, before + ship.anchorFallSpeed * dt);
    if (before < ANCHOR_SPLASH_DEPTH && ship.anchorDepth >= ANCHOR_SPLASH_DEPTH) events |= ANCHOR_SPLASH;
    if (ship.anchorDepth >= 1) {
      ship.anchorFalling = false;
      ship.anchorFallSpeed = 0;
      events |= ANCHOR_BOTTOM;
    }
    return events;
  }
  if (pushers <= 0) return 0;
  ship.anchorDepth = Math.max(0, before - anchorRaiseRate(pushers) * dt);
  const teeth = SHIP.capstanTurns * SHIP.capstanTeeth;
  if (Math.floor(before * teeth) !== Math.floor(ship.anchorDepth * teeth)) events |= ANCHOR_CLICK;
  if (ship.anchorDepth <= 0) {
    ship.anchored = false;
    ship.anchorDecel = 0;
    events |= ANCHOR_STOWED;
  }
  return events;
}

/** Capstan bar geometry, ship-local (tools/models/ship.py `capstanRotor`). */
export const CAPSTAN = {
  bars: 4,
  /** Bar centre-line height. */
  barY: QD + 0.92,
  /** Bars run from the head out to this radius. */
  barReach: 0.85,
  /** Both hands grip a bar around this radius, `gripSpread` either side… */
  gripRadius: 0.55,
  gripSpread: 0.16,
  /** …with the pusher's feet this far behind the bar. */
  standBack: 0.45,
  /** Pushers squeeze past the starboard rail rather than stand in it. */
  maxX: 3 - RAIL_T - PLAYER.radius,
  /** Deck pawl hinge (tools/models/ship.py `capstanPawl`), kicked to drop the anchor. */
  pawl: { x: 1.5, z: -7.56 },
} as const;

/** Capstan head rotation about +y for an anchor depth: raising winds it one way, a drop spins it back. */
export function capstanTurn(depth: number): number {
  return (1 - depth) * SHIP.capstanTurns * Math.PI * 2;
}

export interface CapstanStation { x: number; y: number; z: number; yaw: number }

/**
 * Ship-local feet and facing of the crewmate pushing `bar` at head rotation `turn`: behind the
 * bar, chest square to it, walking the way the bars sweep while the anchor comes up.
 */
export function capstanStation(bar: number, turn: number, out: CapstanStation): CapstanStation {
  // Bar k points along rotor-local +x turned by kπ/2; rotation.y = turn sweeps it to angle kπ/2 − turn.
  const a = (bar * Math.PI) / 2 - turn, ux = Math.cos(a), uz = Math.sin(a);
  const tx = uz, tz = -ux;
  out.x = Math.min(CAPSTAN.maxX, SHIP_POINTS.capstan.x + ux * CAPSTAN.gripRadius - tx * CAPSTAN.standBack);
  out.y = QD;
  out.z = SHIP_POINTS.capstan.z + uz * CAPSTAN.gripRadius - tz * CAPSTAN.standBack;
  out.yaw = Math.atan2(tx, tz);
  return out;
}

/** Bar index of a `capstan:<bar>` station, or -1. */
export function capstanBar(station: string): number {
  if (!station.startsWith("capstan:")) return -1;
  const bar = Number(station.slice(8));
  return Number.isInteger(bar) && bar >= 0 && bar < CAPSTAN.bars ? bar : -1;
}
